import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAssistants } from '../store/app/assistantSlice';
import { fetchCalls } from '../store/app/conversations/conversationSlice';
import { convertToIndianTimeZone, getEndedReasonType, getReasonColorClass } from '../utils/utils';
import { ConversationAnalysis } from './child/ConversationAnalysis';
const ShimmerRows = ({ rows = 5, cols = 11 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`shimmer-r-${r}`}>
        {Array.from({ length: cols }).map((_, c) => {
          const shape =
            c === 0 ? 'shimmer-text' :
            c === 5 ? 'shimmer-badge' :
            c === cols - 1 ? 'shimmer-actions' :
            'shimmer-text';
          return (
            <td key={`shimmer-c-${c}`}>
              <div className={`shimmer ${shape}`} />
            </td>
          );
        })}
      </tr>
    ))}
  </>
);

const CallLogsLayer = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [analysisModal, setAnalysisModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [defaultAssistant, setDefaultAssistant] = useState(null);
  const [currentCallId, setCurrentCallId] = useState(null);
  const [paginatedData, setPaginatedData] = useState([]);
  const [lastCursor, setLastCursor] = useState(null);
  const [filterAssistantId, setFilterAssistantId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const listOfAssistants = useSelector((state) => state.assistants.value);
  const userOrgId = useSelector((state) => state?.user?.user?.organizationId);

  const ITEMS_PER_PAGE = 100;
  const reqIdRef = useRef(0);

  // Load assistants once we have org
  useEffect(() => {
    if (!userOrgId) return;
    dispatch(fetchAssistants(userOrgId));
  }, [dispatch, userOrgId]);

  // Initialize assistant from URL or default to first in list.
  useEffect(() => {
    if (isInitialized) return;

    // When assistants list is not yet known, wait
    if (listOfAssistants === undefined) return;

    const assistantIdFromURL = searchParams.get('assistantId');

    // If no assistants at all -> fallback; stop loading and skip fetching
    if (!listOfAssistants || listOfAssistants.length === 0) {
      setDefaultAssistant(null);
      setFilterAssistantId(null);
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }

    // Try to match from URL, else pick first
    const found =
      assistantIdFromURL
        ? listOfAssistants.find((a) => a?.id === assistantIdFromURL)
        : null;

    const selected = found || listOfAssistants[0];

    setDefaultAssistant(selected);
    setFilterAssistantId(selected.id);

    // Ensure URL reflects the chosen assistant
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('assistantId', selected.id);
      return np;
    });

    setIsInitialized(true);
  }, [isInitialized, listOfAssistants, searchParams, setSearchParams]);

  // Fetch calls when filterAssistantId changes and component is initialized
  useEffect(() => {
    if (!isInitialized || !filterAssistantId) return;

    // If there are no assistants, do not fetch and keep empty table
    if (!listOfAssistants || listOfAssistants.length === 0) {
      setIsLoading(false);
      setPaginatedData([]);
      setLastCursor(null);
      return;
    }

    const fetchCallsData = async () => {
      // start guarded request
      reqIdRef.current += 1;
      const myReq = reqIdRef.current;

      // clear and show shimmer immediately
      setIsLoading(true);
      setPaginatedData([]);
      setCurrentPage(1);
      setLastCursor(null);

      try {
        const payload = { assistantId: filterAssistantId };
        const data = await dispatch(fetchCalls(payload)).unwrap();

        if (myReq !== reqIdRef.current) return; // stale, ignore

        const first = Array.isArray(data) ? data.slice(0, ITEMS_PER_PAGE) : [];
        setPaginatedData(first.length ? [first] : []);
        setCurrentPage(1);
        setLastCursor(
          Array.isArray(data) && data.length === ITEMS_PER_PAGE
            ? data[data.length - 1]?.createdAt ?? null
            : null
        );
      } catch (err) {
        if (myReq !== reqIdRef.current) return;
        setPaginatedData([]);
        setLastCursor(null);
      } finally {
        if (myReq !== reqIdRef.current) return;
        setIsLoading(false);
      }
    };

    fetchCallsData();
  }, [filterAssistantId, dispatch, isInitialized, listOfAssistants]);

  // Open analysis modal from URL
  useEffect(() => {
    const callIdFromURL = searchParams.get('callId');
    if (callIdFromURL) {
      setCurrentCallId(callIdFromURL);
      setAnalysisModal(true);
    }
  }, [searchParams]);

  const handleAssistantChange = (assistantId) => {
    // invalidate any in-flight requests
    reqIdRef.current += 1;

    // show shimmer right away
    setIsLoading(true);
    setPaginatedData([]);
    setCurrentPage(1);
    setLastCursor(null);

    const selected = listOfAssistants.find((a) => a.id === assistantId);
    if (!selected) return;

    setDefaultAssistant(selected);
    setFilterAssistantId(selected.id);
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('assistantId', selected.id);
      np.delete('callId');
      return np;
    });
  };

  const handlePageNext = () => {
    if (paginatedData.length > currentPage) {
      setCurrentPage((p) => p + 1);
      return;
    }
    if (!lastCursor) return;

    // guarded pagination request
    reqIdRef.current += 1;
    const myReq = reqIdRef.current;

    const payload = { createdAt: lastCursor };
    if (filterAssistantId) payload.assistantId = filterAssistantId;

    setIsLoading(true);
    dispatch(fetchCalls(payload))
      .unwrap()
      .then((data) => {
        if (myReq !== reqIdRef.current) return; 
        const next = Array.isArray(data) ? data.slice(0, ITEMS_PER_PAGE) : [];
        if (next.length) {
          setPaginatedData((prev) => [...prev, next]);
          setCurrentPage((p) => p + 1);
        }
        setLastCursor(
          Array.isArray(data) && data.length === ITEMS_PER_PAGE
            ? data[data.length - 1]?.createdAt ?? null
            : null
        );
      })
      .catch(() => {})
      .finally(() => {
        if (myReq !== reqIdRef.current) return;
        setIsLoading(false);
      });
  };

  const getDuration = (start, end) => {
    if (!start || !end) return '-';
    const duration = Math.floor((new Date(end) - new Date(start)) / 1000);
    const min = Math.floor(duration / 60);
    const sec = duration % 60;
    return `${min}m ${sec}s`;
  };

  const currentPageData = paginatedData[currentPage - 1] || [];
  const hasAssistants = Array.isArray(listOfAssistants) && listOfAssistants.length > 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="card h-100 p-0 radius-12">
      {/* Header */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-2">
        <div className="" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <label htmlFor="assistant-select">Assistant:</label>

          {hasAssistants ? (
            <div className="position-relative" style={{ flex: 1, rowGap: '10px' }}>
              <Icon
                icon="lucide:bot"
                width="18"
                height="18"
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6b7280',
                }}
              />
              <select
                id="assistant-select"
                name="assistant_id"
                className="form-control"
                value={defaultAssistant?.id || ''}
                onChange={(e) => handleAssistantChange(e.target.value)}
                required
                style={{ paddingLeft: '36px', paddingRight: '30px', height: '45px', borderRadius: '4px', border: '1px solid #ced4da', backgroundColor: '#fff', width: '100%' }}
              >
                {listOfAssistants.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>

              <Icon
                icon="lucide:chevron-down"
                width="18"
                height="18"
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6b7280',
                }}
              />
            </div>
          ) : (
            <div className="d-flex align-items-center text-secondary" style={{ minHeight: 45 }}>
              <span className="badge bg-secondary-subtle text-secondary-emphasis">No assistants available</span>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th className="text-center">Assistant Name</th>
                <th className="text-center">Organization Name</th>
                <th className="text-center">Call Type</th>
                <th className="text-center">Assistant Phone</th>
                <th className="text-center">Customer Phone</th>
                <th className="text-center">Ended Reason</th>
                <th className="text-center">Evaluation</th>
                <th className="text-center">Duration</th>
                <th className="text-center">Call Initiated At</th>
                <th className="text-center">Status</th>
                <th className="text-center">Details</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <ShimmerRows rows={5} cols={11} />
              ) : currentPageData.length > 0 ? (
                currentPageData.map((item) => {
                  const reasonType = getEndedReasonType(item.endedReason);
                  const reasonColor = getReasonColorClass(reasonType);

                  const evalValue = String(item?.analysis?.successEvaluation);
                  const evalColor =
                    evalValue === 'true'
                      ? 'var(--bs-success)'
                      : evalValue === 'false'
                      ? 'var(--bs-danger)'
                      : 'white';
                  const evalText = evalValue === 'true' ? 'True' : evalValue === 'false' ? 'False' : '-';

                  return (
                    <tr key={item.id}>
                      <td className="text-center">{item?.assistantName}</td>
                      <td className="text-center">{item?.organizationName}</td>
                      <td className="text-center">{item.type}</td>
                      <td className="text-center">
                        {item?.phoneNumber === 'Unknown' ? '-' : item?.phoneNumber?.slice(0, 12) || '-'}
                      </td>
                      <td className="text-center">{item?.customer?.number?.slice(0, 13) || '-'}</td>
                      <td className="text-center">
                        <span 
                          style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                        >
                          <span className={`badge ${reasonColor}`}>
                            {item?.endedReason?.length > 19
                              ? `${item?.endedReason?.slice(0, 19)}..`
                              : item?.endedReason}
                          </span>
                          <span
                            style={{
                              visibility: 'hidden',
                              opacity: 0,
                              width: 'max-content',
                              backgroundColor: '#222',
                              color: '#fff',
                              textAlign: 'center',
                              borderRadius: 6,
                              padding: '5px 8px',
                              position: 'absolute',
                              zIndex: 1,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              bottom: '130%',
                              transition: 'opacity 0.2s',
                              whiteSpace: 'nowrap',
                              fontSize: 13,
                            }}
                            className="reason-tooltip"
                          >
                            {item?.endedReason}
                          </span>
                        </span>
                        <style>
                          {`
                            .reason-tooltip {
                              pointer-events: none;
                            }
                            td .badge:hover + .reason-tooltip,
                            td .badge:focus + .reason-tooltip {
                              visibility: visible !important;
                              opacity: 1 !important;
                            }
                            /* For the combo span, handle hover at parent level */
                            td .badge:hover ~ .reason-tooltip,
                            td span:hover .reason-tooltip {
                              visibility: visible !important;
                              opacity: 1 !important;
                            }
                          `}
                        </style>
                      </td>
                      <td className="text-center">
                        <span className="badge" style={{ backgroundColor: evalColor, color: evalColor === 'white' ? 'black' : 'white' }}>
                          {evalText}
                        </span>
                      </td>
                      <td className="text-center">{getDuration(item.startedAt, item.endedAt)}</td>
                      <td className="text-center">{convertToIndianTimeZone(item.createdAt)}</td>
                      <td className="text-center">{item.status[0].toUpperCase() + item.status.slice(1)}</td>
                      <td className="text-center">
                        <button
                          onClick={() => {
                            setCurrentCallId(item.id);
                            setAnalysisModal(true);
                            const np = new URLSearchParams(searchParams);
                            np.set('callId', item.id);
                            setSearchParams(np);
                          }}
                          className="bg-info-focus text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          style={{ border: 'none', cursor: 'pointer' }}
                        >
                          <Icon icon="mdi-light:fullscreen" className="icon text-xl" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11}>
                    <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '40vh' }}>
                      <Icon icon="icon-park-outline:file-search" className="text-3xl mb-3 text-muted" />
                      <p className="text-secondary-light text-md mb-0">
                        {hasAssistants ? 'No call logs found' : 'No assistants available'}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-10">
          <div className="d-flex gap-2 ms-auto" style={{ justifyContent: 'flex-end' }}>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={isLoading || currentPage === 1}
            >
              Prev
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handlePageNext}
              disabled={isLoading || (!lastCursor && paginatedData.length === currentPage)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {analysisModal && (
        <ConversationAnalysis
          callId={currentCallId}
          setAnalysisModal={(val) => {
            setAnalysisModal(val);
            if (!val) {
              const np = new URLSearchParams(searchParams);
              np.delete('callId');
              setSearchParams(np);
              setCurrentCallId(null);
            }
          }}
        />
      )}
    </motion.div>
  );
};
export default CallLogsLayer;